import { authClient } from '@/lib/auth-client';

/**
 * API Key Service
 *
 * Handles all API key-related business logic and operations.
 * Provides a centralized interface for API key management.
 */

export interface ApiKey {
  id: string;
  name: string;
  start: string;
  prefix: string;
  userId: string;
  refillInterval: number | null;
  refillAmount: number | null;
  lastRefillAt: string | Date | null;
  enabled: boolean;
  rateLimitEnabled: boolean;
  rateLimitTimeWindow: number | null;
  rateLimitMax: number | null;
  requestCount: number;
  remaining: number | null;
  lastRequest: string | Date | null;
  expiresAt: string | Date | null;
  createdAt: string | Date;
  updatedAt: string | Date;
  permissions: string | null;
  metadata: any | null;
}

export interface CreateApiKeyData {
  name: string;
  expiresIn?: number;
  permissions?: any;
}

export interface CreateApiKeyResponse {
  key: string;
  [key: string]: any;
}

export const apiKeyService = {
  /**
   * List all API keys for the current user
   */
  async list(): Promise<ApiKey[]> {
    const response = await authClient.apiKey.list({});
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to fetch API keys');
    }
    return response.data || [];
  },

  /**
   * Create a new API key
   */
  async create(data: CreateApiKeyData): Promise<CreateApiKeyResponse> {
    const response = await authClient.apiKey.create(data);
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to create API key');
    }
    return response.data;
  },

  /**
   * Delete an API key
   */
  async delete(keyId: string): Promise<void> {
    const response = await authClient.apiKey.delete({
      keyId,
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to delete API key');
    }
  },

  /**
   * Verify an API key
   */
  async verify(key: string): Promise<{ valid: boolean }> {
    const response = await authClient.apiKey.verify({
      key,
    });
    if (response?.error) {
      throw new Error(response.error.message || 'Failed to verify API key');
    }
    return response.data || { valid: false };
  },

  /**
   * Create a service-scoped API key
   * Service binding is stored in the metadata field
   */
  async createForService(
    serviceId: string,
    data: CreateApiKeyData
  ): Promise<CreateApiKeyResponse> {
    const response = await fetch(`/api/services/${serviceId}/api-keys/create-and-bind`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create service API key');
    }

    return response.json();
  },

  /**
   * List API keys for a specific service
   * Filters by metadata.serviceId
   */
  async listForService(serviceId: string): Promise<ApiKey[]> {
    const response = await fetch(`/api/services/${serviceId}/api-keys`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to load API keys: ${response.statusText}`);
    }

    const data = await response.json();
    return data.apiKeys || [];
  },
};
